'use client';

import React, { createContext, useContext, useReducer, useCallback } from 'react';
import { ariaReducer, initialState } from './reducer';
import { ariaActions, createAsyncAction } from './actions';
import { AriaContextType, AriaProviderProps } from './types';
import { ChatMessage, AnimationType, VoiceState } from '@/types';
import { useWebSpeech } from '@/components/Aria/hooks/useWebSpeech';
import { useVoiceProcessing } from '@/components/Aria/hooks/useVoiceProcessing';

const AriaContext = createContext<AriaContextType | null>(null);

export const useAria = () => {
  const context = useContext(AriaContext);
  if (!context) {
    throw new Error('useAria must be used within an AriaProvider');
  }
  return context;
};

export const AriaProvider: React.FC<AriaProviderProps> = ({ 
  children, 
  initialConfig 
}) => {
  const mergedInitialState = {
    ...initialState,
    config: {
      ...initialState.config,
      animation: {
        ...initialState.config.animation,
        ...(initialConfig?.animation ? {
          currentState: initialConfig.animation.currentState || initialState.config.animation.currentState,
          previousState: initialConfig.animation.previousState,
          isPlaying: initialConfig.animation.isPlaying || false,
          format: initialConfig.animation.format || 'video',
          currentSource: initialConfig.animation.currentSource || null
        } : {})
      },
      voice: {
        ...initialState.config.voice,
        ...(initialConfig?.voice || {})
      }
    }
  };

  const [state, dispatch] = useReducer(ariaReducer, mergedInitialState);
  const voiceProcessor = useWebSpeech();

  const setAnimationState = useCallback((animationType: AnimationType) => {
    dispatch(ariaActions.setAnimationState(animationType));
  }, []);

  const setVoiceState = useCallback((voiceState: Partial<VoiceState>) => {
    dispatch(ariaActions.setVoiceState(voiceState));
  }, []);

  const sendMessage = useCallback(async (message: string, format: 'text' | 'voice') => {
    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: message,
      timestamp: new Date(),
      format,
      type: 'user'
    };

    dispatch(ariaActions.addMessage(userMessage));

    try {
      // Use the API endpoint instead of messageProcessor
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ message, format }),
      });

      const data = await response.json();
      
      const assistantMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: data.content,
        timestamp: new Date(),
        format: 'text',
        type: 'assistant'
      };

      dispatch(ariaActions.addMessage(assistantMessage));

      if (format === 'voice' && state.voiceEnabled) {
        await voiceProcessor.speak(data.content);
      }

      return data.content;
    } catch (error) {
      dispatch(ariaActions.setError({
        code: 'MESSAGE_PROCESSING_ERROR',
        message: error instanceof Error ? error.message : 'Failed to process message',
        recoverable: true
      }));
      throw error;
    }
  }, [voiceProcessor, state.voiceEnabled, dispatch]);

  const startVoiceChat = useCallback(async () => {
    try {
      await voiceProcessor.initialize();
      setVoiceState({
        isAvailable: true,
        isMicrophoneAvailable: true,
        isProcessing: false,
        isSpeaking: false
      });
      dispatch(ariaActions.setVoiceEnabled(true));
      setAnimationState('listening');
    } catch (error) {
      dispatch(ariaActions.setError({
        code: 'VOICE_INITIALIZATION_ERROR',
        message: error instanceof Error ? error.message : 'Failed to initialize voice',
        recoverable: true
      }));
    }
  }, [voiceProcessor, setAnimationState, setVoiceState]);

  const stopVoiceChat = useCallback(() => {
    dispatch(ariaActions.setVoiceEnabled(false));
    setAnimationState('idle');
    voiceProcessor.stop();
  }, [voiceProcessor, setAnimationState]);

  const minimizeAria = useCallback(() => {
    dispatch(ariaActions.setMinimized(true));
    if (state.voiceEnabled) {
      stopVoiceChat();
    }
  }, [state.voiceEnabled, stopVoiceChat]);

  const maximizeAria = useCallback(() => {
    dispatch(ariaActions.setMinimized(false));
  }, []);

  const contextValue: AriaContextType = {
    state,
    dispatch,
    sendMessage,
    startVoiceChat,
    stopVoiceChat,
    setAnimationState,
    minimizeAria,
    maximizeAria
  };

  return (
    <AriaContext.Provider value={contextValue}>
      {children}
    </AriaContext.Provider>
  );
};